import { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import Button from '@/components/Button/Button'
import UIIcon, { UIIconType } from '@/components/UI/UIIcon'
import { useAppState } from '@/composables/useAppState'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import useTracking from '@/composables/useTracking'
import { Persona } from '@/state/AppConfig'
import { AppNextEvent } from '@/state/AppEvent'
import { getContentfulUrl } from '@/utils/contentful'

export type KidCardProps = {
  persona: Persona
  leaving?: boolean
  setLeaving: (leaving: boolean) => void
}

const icons = {
  pedro: 'clover',
  caris: 'diamond',
  francesca: 'explosion',
}
const colors = {
  pedro: 'text-coral',
  caris: 'text-light-blue',
  francesca: 'text-mid-blue',
}

const zooms = {
  pedro: 'scale-[2500%] rotate-90',
  caris: 'scale-[1500%] rotate-90',
  francesca: 'scale-[2500%] rotate-90',
}

export default function KidCard({ persona, leaving, setLeaving }: KidCardProps) {
  const { t } = useTranslation()

  const [hover, setHover] = useState<boolean>(false)
  const [active, setActive] = useState<boolean>(false)

  const { actor, persona: currentPersona, scenarioUnitName } = useAppState()
  const { trackClick } = useTracking()

  const onClick = useCallback(() => {
    trackClick({
      clickType: 'button',
      unitName: `${currentPersona}_simulation${scenarioUnitName}`,
      unitVariation: 'summary',
      unitLocation: 'tyce',

      buttonText: `meet_${persona}`,
      buttonName: 'navigation_button',
    })

    setLeaving(true)
    setActive(true)
    setTimeout(() => {
      actor.send({ type: 'NEXT', persona: persona } as AppNextEvent)
    }, 700)
  }, [setLeaving, actor, persona, currentPersona, trackClick, scenarioUnitName])

  const onHoverStart = useCallback(() => {
    setHover(true)
  }, [])
  const onHoverStop = useCallback(() => {
    setHover(false)
  }, [])

  const betterFormat = useImageFormatContext()
  const [imageUrl, setImageUrl] = useState(null)
  useEffect(() => {
    const url = getContentfulUrl(`/src/assets/images/meet/${persona}.png`, { w: 608, fm: betterFormat })
    setImageUrl(url)
  }, [persona, betterFormat])

  return (
    <div
      className={`KidCard w-full flex flex-col items-center ${active ? 'z-1000' : ''} ${leaving && !active ? 'transition-opacity duration-700 ease-out-quad opacity-0' : '0'}`}
    >
      <picture className='relative'>
        <div
          aria-hidden='true'
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-320 md:text-384 ${colors[persona]} transition-transform ease-out-quad ${hover ? 'scale-110 rotate-12 duration-1000' : 'duration-200'}`}
        >
          <UIIcon
            icon={icons[persona] as UIIconType}
            className={`transition-transform duration-700 ease-out-quart ${active ? zooms[persona] : ''}`}
          />
          {/* <UIIcon
            icon={icons[persona] as UIIconType}
            className={`absolute top-0 left-0 w-full h-full text-cream transition-transform duration-700 delay-100 ease-out-quart ${active ? zooms[persona] : 'scale-[0.1%]'}`}
          /> */}
        </div>
        <div
          className={`relative transition-opacity duration-500 ease-out-quad ${active ? 'opacity-0 pointer-events-none' : ''}`}
        >
          <img
            className={`block relative w-[243px] md:w-[304px] h-auto aspect-304/345 -mb-2 transition-transform origin-bottom ease-out-quad ${hover ? 'scale-95 duration-1000' : 'duration-200'}`}
            src={imageUrl}
            alt={t(`child.${persona}.name`)}
          />
        </div>
      </picture>
      <Button
        onClick={onClick}
        className={`relative transition-opacity duration-500 ease-out-quad ${active ? 'opacity-0 pointer-events-none' : ''}`}
      >
        <Trans values={{ persona: t(`child.${persona}.name`) }}>summary.meet.cta</Trans>
      </Button>
    </div>
  )
}
